/**
 * Sistema unificado de manejo de tokens JWT para psicólogos
 * Usa sessionStorage para seguridad (se elimina al cerrar navegador)
 * Incluye validación de expiración y renovación automática
 */

const TOKEN_KEY = 'psi-auth-token';
const SESSION_KEY = 'psi-session-data';

// Solo desarrollo: trata la sesión impersonada como un login normal de psicóloga, para
// poder probar la UI sin los bypasses de CX (popup de cambio de ubicación, reglas de
// 48h/6h, días pasados). No afecta a `getImpersonatorId`: el backend sigue recibiendo el
// impersonator_id real, así que la auditoría de quién agendó queda intacta.
const IGNORE_IMPERSONATION = process.env.REACT_APP_IGNORE_IMPERSONATION === 'true';

export interface JWTUserData {
  id: string;
  email: string;
  name: string;
  surname: string;
}

export interface JWTPayload {
  id: string;
  email?: string;
  name?: string;
  psychologist_id: string;
  scope: string[];
  platform: 'psi-platform';
  iat: number;
  exp: number;
  impersonator_id?: string;
}

export interface SessionData {
  token: string;
  user: JWTUserData;
  psychologist_id: string | null;
  scopes: string[];
  expiresAt: number;
}

function parseJWTPayload(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));

    if (payload.platform !== 'psi-platform') {
      console.error('❌ Token no es de psi-platform');
      return null;
    }

    if (!payload.psychologist_id) {
      console.error('❌ Token no contiene psychologist_id');
      return null;
    }

    return payload as JWTPayload;
  } catch {
    return null;
  }
}

function isTokenExpired(expiresAt: number): boolean {
  const now = Math.floor(Date.now() / 1000);
  const buffer = 60;
  return now >= (expiresAt - buffer);
}

/**
 * Guardar sesión completa con validación
 * SOLO guarda información de la respuesta de API Core, no de Google
 */
export function saveSession(token: string, userData: JWTUserData, scopes: string[]): boolean {
  try {
    const payload = parseJWTPayload(token);
    if (!payload) {
      console.error('❌ Token JWT inválido');
      return false;
    }

    if (!payload.exp) {
      console.error('❌ Token sin fecha de expiración');
      return false;
    }

    const sessionData: SessionData = {
      token,
      user: userData,
      psychologist_id: payload.psychologist_id,
      scopes: Array.isArray(payload.scope) ? payload.scope : scopes,
      expiresAt: payload.exp
    };

    // Guardar en ambas storages: sessionStorage (compat) y localStorage (cross-tab)
    try { sessionStorage.setItem(TOKEN_KEY, token); } catch { void 0; }
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData)); } catch { void 0; }
    try { localStorage.setItem(TOKEN_KEY, token); } catch { void 0; }
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData)); } catch { void 0; }

    return true;
  } catch (error) {
    console.error('❌ Error guardando sesión:', error);
    return false;
  }
}

/**
 * Obtener token si es válido y no está expirado
 */
export function getValidToken(): string | null {
  try {
    const sessionData = getSessionData();
    if (!sessionData) return null;

    if (isTokenExpired(sessionData.expiresAt)) {
      clearSession();
      return null;
    }

    return sessionData.token;
  } catch (error) {
    console.error('❌ Error obteniendo token:', error);
    return null;
  }
}

/**
 * Obtener datos completos de la sesión
 */
export function getSessionData(): SessionData | null {
  try {
    // Intentar leer primero desde sessionStorage (compat), luego desde localStorage (cross-tab)
    const raw = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    
    const sessionData = JSON.parse(raw) as SessionData;
    
    if (isTokenExpired(sessionData.expiresAt)) {
      clearSession();
      return null;
    }
    
    return sessionData;
  } catch (error) {
    console.error('❌ Error obteniendo datos de sesión:', error);
    return null;
  }
}

/**
 * Obtener psychologist_id de la sesión
 */
export function getPsychologistId(): string | null {
  const sessionData = getSessionData();
  return sessionData?.psychologist_id || null;
}

/**
 * Verificar si hay una sesión válida (token no expirado)
 */
export function hasValidSession(): boolean {
  const sessionData = getSessionData();
  if (!sessionData) return false;
  
  return !isTokenExpired(sessionData.expiresAt);
}

/**
 * Limpiar sesión completa
 */
export function clearSession(): void {
  try {
    // Limpiar sessionStorage y localStorage
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { void 0; }
    try { sessionStorage.removeItem(SESSION_KEY); } catch { void 0; }
    try { localStorage.removeItem(TOKEN_KEY); } catch { void 0; }
    try { localStorage.removeItem(SESSION_KEY); } catch { void 0; }
    
    // Limpiar localStorage - items específicos
    localStorage.removeItem('psi-connect-user');
    localStorage.removeItem('psi-connect-psychologist');
    localStorage.removeItem('psi-timezone');
    localStorage.removeItem('google_user');
    localStorage.removeItem('google_access_token');
    localStorage.removeItem('oauth_state');
    
    // Limpiar cualquier otro item relacionado con autenticación (psi-, google-, firebase:)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('psi-') || key.startsWith('google-') || key.startsWith('firebase:'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        console.warn(`⚠️ No se pudo eliminar la clave ${key}:`, err);
      }
    });
  } catch (error) {
    console.error('❌ Error limpiando sesión:', error);
  }
}

/**
 * Obtener headers de autorización con token válido
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getValidToken();
  if (token) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }
  return {
    'Content-Type': 'application/json'
  };
}

/**
 * Redirigir al login y limpiar sesión
 */
export function redirectToLogin(): void {
  clearSession();
  window.location.href = '/login';
}

/**
 * Obtener tiempo restante de sesión en segundos
 */
export function getSessionTimeRemaining(): number {
  const sessionData = getSessionData();
  if (!sessionData) return 0;
  
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, sessionData.expiresAt - now);
}

/**
 * Verificar si la sesión actual es una impersonación
 */
export function isImpersonated(): boolean {
  if (IGNORE_IMPERSONATION) return false;
  const token = getValidToken();
  if (!token) return false;
  const payload = parseJWTPayload(token);
  return !!payload?.impersonator_id;
}

/**
 * Obtener el ID del impersonador (si aplica)
 */
export function getImpersonatorId(): string | null {
  const token = getValidToken();
  if (!token) return null;
  const payload = parseJWTPayload(token);
  return payload?.impersonator_id || null;
}

/**
 * Obtener el email del psicólogo desde el JWT
 */
export function getPsychologistEmail(): string | null {
  const token = getValidToken();
  if (!token) return null;
  const payload = parseJWTPayload(token);
  return payload?.email || null;
}

/**
 * Verificar si el usuario es psicólogo
 */
export function isPsychologist(): boolean {
  const sessionData = getSessionData();
  if (!sessionData) return false;
  
  return sessionData.scopes.some(scope => 
    scope.includes('psychologist') || 
    scope.includes('manage:customers')
  ) || sessionData.psychologist_id !== null;
}

/**
 * Función de debugging - información de sesión sin exponer datos sensibles
 */
export function getSessionDebugInfo(): {
  hasSession: boolean;
  isValid: boolean;
  timeRemaining?: number;
  isPsychologist?: boolean;
  psychologistId?: string | null;
  email?: string;
} {
  const sessionData = getSessionData();
  
  if (!sessionData) {
    return {
      hasSession: false,
      isValid: false
    };
  }
  
  return {
    hasSession: true,
    isValid: !isTokenExpired(sessionData.expiresAt),
    timeRemaining: getSessionTimeRemaining(),
    isPsychologist: isPsychologist(),
    psychologistId: sessionData.psychologist_id,
    email: sessionData.user.email
  };
}

// Mantener compatibilidad con código existente
export const getApiCoreToken = getValidToken;
export const isApiCoreAuthenticated = hasValidSession;
export const hasActiveSession = hasValidSession;
export const clearApiCoreToken = clearSession;
export const clearCompleteSession = clearSession;
export const getApiCoreAuthHeaders = getAuthHeaders;

// Alias para saveSession
export function saveApiCoreToken(token: string, userData: JWTUserData, scopes: string[]): void {
  saveSession(token, userData, scopes);
}

// Mantener JWTData para compatibilidad
export interface JWTData {
  token: string;
  user: JWTUserData;
  scopes: string[];
  psychologist_id?: string;
}

export function getJWTData(): JWTData | null {
  const sessionData = getSessionData();
  if (!sessionData) return null;
  
  return {
    token: sessionData.token,
    user: sessionData.user,
    scopes: sessionData.scopes,
    psychologist_id: sessionData.psychologist_id || undefined
  };
}
